'use client';

import { useTheme } from '@/components/theme/ThemeProvider';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Monitor, Moon, Sun } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

type ThemeOption = 'system' | 'light' | 'dark';

const themeIcons: Record<ThemeOption, React.ReactNode> = {
  system: <Monitor className='h-4 w-4' />,
  light: <Sun className='h-4 w-4' />,
  dark: <Moon className='h-4 w-4' />,
};

export function ThemeSwitcher() {
  const t = useTranslations('Theme');
  const { theme, setTheme } = useTheme();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const handleThemeChange = (newTheme: string) => {
    setTheme(newTheme as ThemeOption);
  };

  const currentTheme = theme as ThemeOption;

  return (
    <TooltipProvider delayDuration={200}>
      <div className='flex items-center justify-center h-9'>
        <Select value={currentTheme} onValueChange={handleThemeChange}>
          <Tooltip open={isTooltipOpen}>
            <TooltipTrigger asChild>
              <SelectTrigger
                // The trigger renders only a theme icon, so the combobox had no
                // accessible name (#8708, axe button-name). The tooltip is not a
                // substitute — it names the CURRENT theme, and only once open.
                // This names the control itself and stays stable across values.
                aria-label={t('switcherLabel')}
                className='w-[44px] h-9 bg-transparent border-none focus:ring-0 focus:ring-offset-0 p-2 flex items-center justify-center'
                onPointerEnter={() => setIsTooltipOpen(true)}
                onPointerLeave={() => setIsTooltipOpen(false)}
              >
                <SelectValue>{themeIcons[currentTheme]}</SelectValue>
              </SelectTrigger>
            </TooltipTrigger>
            <TooltipContent side='top' align='center' sideOffset={4}>
              <p>{t(currentTheme)}</p>
            </TooltipContent>
          </Tooltip>
          <SelectContent className='w-[50px] min-w-[50px] p-0'>
            <SelectGroup className='p-0'>
              <SelectItem
                value='system'
                className='p-2 pl-2 justify-center [&>span:first-child]:hidden'
                title={t('system')}
              >
                <Monitor className='h-5 w-5' />
              </SelectItem>
              <SelectItem
                value='light'
                className='p-2 pl-2 justify-center [&>span:first-child]:hidden'
                title={t('light')}
              >
                <Sun className='h-5 w-5' />
              </SelectItem>
              <SelectItem
                value='dark'
                className='p-2 pl-2 justify-center [&>span:first-child]:hidden'
                title={t('dark')}
              >
                <Moon className='h-5 w-5' />
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    </TooltipProvider>
  );
}
